import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import {
  RequiredFieldsText,
  RequiredItem,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";

import { OrganizationDropdownList } from "@components/custom/dropdowns/organization-dropdown-list";
import { ProductDropDownList } from "@components/custom/dropdowns/product-dropdown-list";
import { ClassroomDetailsEditor } from "./classroom-details-editor";

import { Role } from "@models";
import { catchUserCreationError } from "@services/user.util";
import { UserOptionItem } from "./user-option-item";

import { ProgramDropDownList } from "@components/custom/dropdowns/program-dropdown-list";
import { BUILD_YOUR_OWN_PROGRAM } from "./new-classroom.utils.js";
import { useCourseBuilderEnabled } from "@hooks/queries";

import container from "@src/container";
import { ProfessionalLearningProduct } from "@components/custom/dropdowns/product-dropdown-list";

export function ClassroomInfo({
  orgSelectionEnabled = true,
  products,
  programs,
  instructorCreationEnabled = true,
  className = "",
  bulkUploadEnabled = false,
  setSelectedOrg,
}) {
  const { userService } = container;

  const { reset, control, setValue, getValues } = useFormContext();

  const [productOptions, setProductsOptions] = useState(products);
  const [stateInstructorData, setStateInstructorData] = useState();
  const [stateTraineeData, setStateTraineeData] = useState();

  const selectedProduct = useWatch({ name: "product" });
  const selectedOrg = useWatch({ name: "organization" });
  const selectedProgram = useWatch({ name: "program" });

  const isProfessionalLearning =
    selectedProduct?.id === ProfessionalLearningProduct.id;

  const courseBuilderEnabled = useCourseBuilderEnabled();

  const programsForProgramDropdown = useMemo(() => {
    const list = programs ?? [];
    if (!isProfessionalLearning) {
      const base = list.filter((p) => !p.professionalLearning);
      return courseBuilderEnabled ? [BUILD_YOUR_OWN_PROGRAM, ...base] : base;
    } else {
      return list.filter((p) => p.professionalLearning);
    }
  }, [programs, isProfessionalLearning, courseBuilderEnabled]);

  useEffect(() => {
    setProductsOptions(products);
  }, [products]);

  async function addTraineesToForm(...trainees) {
    const selectedTrainees = getValues("trainees");
    const updatedTrainees = [...selectedTrainees, ...trainees];

    setValue("trainees", updatedTrainees);
  }

  async function addInstructorsToForm(instructors) {
    const selectedInstructors = getValues("instructors");
    const updatedInstructors = [...selectedInstructors, instructors];

    setValue("instructors", updatedInstructors);
  }

  async function fetchInstructorUsers() {
    const instructorData = await userService.getOrganizationUsersForRoles(
      selectedOrg.code,
      [Role.INSTRUCTOR.id, Role.ORG_ADMIN.id]
    );

    setStateInstructorData(instructorData);
  }

  async function fetchStudentUsers() {
    const studentUsers = await userService.getOrganizationUsersForRole(
      selectedOrg.code,
      Role.STUDENT.id
    );
    setStateTraineeData(studentUsers);
  }

  const onOrgSelected = async (org) => {
    reset({
      name: "",
    });
    setValue("organization", org);
    setSelectedOrg(org.code);
  };

  const onProductSelected = async (product) => {
    await fetchInstructorUsers();
    if (product.id !== ProfessionalLearningProduct.id) {
      await fetchStudentUsers();
    } else {
      setStateTraineeData(undefined);
    }

    setValue("product", product);
  };

  const onProgramSelected = async (selectedProgram) => {
    setValue("program", selectedProgram);
  };

  const onCreateInstructor = async (user) => {
    let instructor;

    try {
      instructor = await userService.createNewUser(user, selectedOrg.code);
    } catch (error) {
      let errorMessage;
      if (error.status == 409) {
        errorMessage = "Email is not unique";
      } else {
        errorMessage =
          error.data && error.data.detail
            ? error.data.detail
            : "Unable to create instructor";
      }

      return { error: errorMessage };
    }

    await fetchInstructorUsers();

    const instructorOption = new UserOptionItem(instructor);
    addInstructorsToForm(instructorOption);

    return instructor;
  };

  const onCreateTrainee = async (user) => {
    let student;

    const productList =
      selectedProduct.id === Product.TRK ||
      selectedProduct.id === Product.CE_TRK
        ? [Product.TRK, Product.CE]
        : selectedProduct.id === "TRK_TS"
        ? [Product.TS, Product.TRK, Product.CE]
        : [selectedProduct.id];

    try {
      student = await userService.createNewUser(
        user,
        selectedOrg.code,
        productList
      );
    } catch (error) {
      return catchUserCreationError(error);
    }

    await fetchStudentUsers();

    const studentOption = new UserOptionItem(student);
    addTraineesToForm(studentOption);

    return student;
  };

  const onAddTrainees = async (users) => {
    if (users && users.length > 0) {
      await fetchStudentUsers();

      const optionItems = users.map((user) => new UserOptionItem(user));
      addTraineesToForm(...optionItems);
    }
  };

  return (
    <div className={className}>
      <div className="text-with-info">
        <RequiredFieldsText />
      </div>
      {orgSelectionEnabled && (
        <div className="organization">
          <div className="section-title">
            <RequiredItem text="Organization" />
          </div>

          <Controller
            control={control}
            name="organization"
            rules={{ required: true }}
            render={({ field: { value } }) => {
              return (
                <OrganizationDropdownList
                  className="org-dropdown"
                  onOrgSelected={(org) => {
                    onOrgSelected(org);
                  }}
                  selectedOrg={value}
                />
              );
            }}
          />
        </div>
      )}
      <div className="classroom-section">
        {(!orgSelectionEnabled || selectedOrg) && (
          <div className="classroom-product">
            <div className="section-title">
              <RequiredItem text="Product" />
            </div>
            <Controller
              control={control}
              name="product"
              rules={{ required: true }}
              render={({ field: { value } }) => {
                return (
                  <ProductDropDownList
                    products={productOptions}
                    className="org-dropdown"
                    onProductSelected={(product) => {
                      onProductSelected(product);
                    }}
                    defaultProduct={value}
                    combineCeTrek
                  />
                );
              }}
            />
          </div>
        )}
      </div>
      {(selectedProduct?.id === Product.PROG || isProfessionalLearning) && (
        <div className="classroom-section">
          {(!orgSelectionEnabled || selectedOrg) && (
            <div className="classroom-program">
              <div className="section-title">
                <RequiredItem text="Program" />
              </div>
              <Controller
                control={control}
                name="program"
                rules={{ required: true }}
                render={({ field: { value } }) => {
                  return (
                    <ProgramDropDownList
                      programs={programsForProgramDropdown}
                      className="org-dropdown"
                      onProgramSelected={(program) => {
                        onProgramSelected(program);
                      }}
                      defaultProgram={value}
                    />
                  );
                }}
              />
            </div>
          )}
          <div className="program-description">
            {selectedProgram?.description}
          </div>
        </div>
      )}
      <div className="info-section">
        {(!orgSelectionEnabled || selectedOrg) &&
          selectedProduct &&
          stateInstructorData &&
          (isProfessionalLearning || stateTraineeData) && (
            <div className="user-details">
              <div className="section-title">
                <RequiredItem text="Classroom Details" />
              </div>
              <ClassroomDetailsEditor
                instructorData={stateInstructorData}
                onCreateInstructor={onCreateInstructor}
                traineeData={
                  isProfessionalLearning
                    ? stateInstructorData
                    : stateTraineeData
                }
                onCreateTrainee={onCreateTrainee}
                instructorCreationEnabled={instructorCreationEnabled}
                onAddTrainees={onAddTrainees}
                bulkUploadEnabled={bulkUploadEnabled}
                isProfessionalLearning={isProfessionalLearning}
              />
            </div>
          )}
      </div>
    </div>
  );
}
